/* eslint-disable no-console */
import React, { useState } from 'react';
import { FacetAccordion } from '@components';
import { Skeleton, Input, Label } from 'gd-design-library';
import styles from './PriceFilterFacet.module.scss';
import classnames from 'classnames/bind';
import { CategoryPriceFacet } from '@services/category/interfaces';
import { TermInterval, Facet } from '@services/product/interfaces';

const cx = classnames.bind(styles);

const CN = 'facet-prices';
const priceCountClassName = `${CN}__price-count`;
const priceNameClassName = `${CN}__price-name`;
const labelClassName = `${CN}__label`;
const labelTextClassName = `${CN}__label-text`;
const skeletonClassName = `${CN}__skeleton`;

type Prices = [from: number, to: number];

interface Props {
  facets?: Facet[];
  facetLabelsByName?: Record<string, string | undefined>;
  isAWSEnv?: boolean;
  facet?: string; // The facet name (e.g., 'price' or 'price.centAmount')
  selectedTerms: string[];
  selectTerm: (term: string) => void;
  deleteTerm: (term: string) => void;
  isLoading?: boolean;
  withAccordion?: boolean;
}

const getPriceFacet = (facets: Facet[] | undefined, isAWSEnv: boolean): Facet | undefined =>
  facets?.find(item => isAWSEnv ? item.facet.match('price.centAmount') : item.facet.match('price'));

const getLabel = (
  facet: Facet | undefined,
  isAWSEnv: boolean,
  facetLabelsByName: Record<string, string | undefined> | undefined
): string => {
  if (!facet) return '';

  return isAWSEnv ? (facetLabelsByName?.[facet.facet] ?? '') : (facet.displayName ?? '');
};

const transformPriceFacetData = (term: TermInterval): CategoryPriceFacet => ({
  count: term.count,
  from: term.interval.minimum,
  to: term.interval.maximum
});

export const PriceFilterFacet: React.FC<Props> = ({
  facets,
  facetLabelsByName,
  isAWSEnv = false,
  selectedTerms,
  selectTerm,
  deleteTerm,
  isLoading,
  withAccordion = true
}) => {
  const facetData = getPriceFacet(facets, isAWSEnv);
  const label = getLabel(facetData, isAWSEnv, facetLabelsByName);

  const ranges = (facetData?.value.terms as TermInterval[])?.filter((term): term is TermInterval =>
    'interval' in term && typeof term.interval === 'object'
  ).map(transformPriceFacetData) ?? [];

  const [ checked, setChecked ] = useState<{ id: string, range: Prices, term: string }[]>(
    () => selectedTerms
      .map(termStr => {
        try {
          const parsed = JSON.parse(termStr);

          if (Array.isArray(parsed) && parsed.length === 2 &&
              typeof parsed[0] === 'number' && typeof parsed[1] === 'number') {
            const [ from, to ] = parsed as Prices;

            return { id: `${from}${to}`, range: [ from, to ] as Prices, term: termStr };
          }
        } catch (error) {
          console.error(`Failed to parse range term: "${termStr}"`, error);
        }

        return null;
      })
      .filter((item): item is { id: string, range: Prices, term: string } => item !== null)
  );

  const getCheckHandler = (range: Prices, internalId: string, term: string) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      let newChecked;

      if (event.target.checked) {
        newChecked = [ ...checked, { id: internalId, range, term } ];
        selectTerm(term);
      } else {
        newChecked = checked.filter(({ id }) => id !== internalId);
        deleteTerm(term);
      }
      setChecked(newChecked);
    };

  const content = !isLoading ? ranges.map(({ from, to, count }) => {
    const internalId = `${from}${to}`;
    const value = `$${Math.ceil(from)} - $${Math.ceil(to)}`;
    const term = JSON.stringify([ from, to ]);

    return (
      <Label key={ internalId } className={ cx(labelClassName) }>
        <Input
          role='checkbox'
          variant='checkbox'
          className={ cx(priceNameClassName) }
          checked={ checked.some(({ id }) => id === internalId) }
          onChange={ getCheckHandler([ from, to ], internalId, term) }
          disabled={ !count }
        />
        <div className={ cx(labelTextClassName) }>
          {value} <span className={ cx(priceCountClassName) }>({count})</span>
        </div>
      </Label>
    );
  }) : (
    <>
      <Skeleton height='24px' className={ cx(skeletonClassName) } />
      <Skeleton height='24px' className={ cx(skeletonClassName) } />
      <Skeleton height='24px' className={ cx(skeletonClassName) } />
    </>
  );

  return (
    withAccordion ? (
      <FacetAccordion label={ label }>
        {content}
      </FacetAccordion>
    ) : <>{content}</>);
};
